import {
  LegalLayout,
  Section,
  SubList,
} from "../components/legal/LegalLayout";

export default function Privacy() {
  return (
    <LegalLayout
      title="Privacy Policy"
      updated="August 11, 2026"
      pageTestId="page-privacy"
    >
      <Section title="1. Who we are">
        <SubList
          items={[
            "Data controller: IVORY PULSE SRL",
            "Registration number: 52619680",
            "General contact: contact@ivory-pulse.com",
            "Customer support: support@ivory-pulse.com",
            "Founders Edition / sales: sales@ivory-pulse.com",
            "Registered office: Romania",
          ]}
        />
      </Section>

      <Section title="2. What data we collect">
        <p>
          Depending on how you use the site, we may collect the following
          personal data:
        </p>
        <SubList
          items={[
            "Waitlist data: email address, optional first name, selected finish (Obsidian Black / Champagne Gold / Chrome Silver), signup timestamp, and a record of your marketing consent (the exact text you agreed to plus the timestamp).",
            "Source / page context: the referring URL or page that led you to submit the form.",
            "Technical data: IP address, device type, browser, operating system, approximate region, and diagnostic data strictly necessary to run and secure the service.",
            "Voice-demo data (only when you tap Talk to Pulse): the microphone audio you record, a short-lived session ID, and the resulting transcript + AI reply.",
            "Order and payment metadata (once pre-sale is enabled): billing name, shipping address, order contents, order status, and a payment reference provided by our payment provider. Full card details are handled by the payment provider and are not stored on our servers.",
            "Support communications: any information you send us by email when requesting help.",
          ]}
        />
      </Section>

      <Section title="3. Voice demo processing">
        <p>
          When you use the live voice demo, your audio is sent to our backend
          and forwarded to trusted AI service providers for speech
          transcription, language understanding and text-to-speech generation.
          The resulting audio response is returned to you through our
          same-origin audio proxy. We use this data only to operate the demo,
          to diagnose service issues and to improve voice-quality reliability.
          We do not sell or advertise against voice data.
        </p>
      </Section>

      <Section title="4. Payments">
        <p>
          Once payments are enabled, card and billing information is collected
          and processed by our payment provider (for example, Stripe) under
          its own privacy terms. We receive only limited transaction metadata
          (such as success/failure, order reference, last four digits) and do
          not store full card numbers on our servers.
        </p>
      </Section>

      <Section title="5. Why we use your data">
        <SubList
          items={[
            "Manage the waitlist and notify you when pre-sale opens or your spot is available.",
            "Provide the voice demo, the AI companion experience, and the optional companion app.",
            "Process pre-orders and fulfil shipments once payments are enabled.",
            "Provide customer support and respond to your questions.",
            "Send operational updates about your reservation, shipment or product.",
            "Improve reliability, safety and quality of the service.",
            "Comply with applicable legal, tax and consumer-protection obligations.",
          ]}
        />
      </Section>

      <Section title="6. Legal basis (for users in the EU/EEA and UK)">
        <SubList
          items={[
            "Consent — for the waitlist email, optional marketing updates, and the voice demo (you trigger each recording yourself).",
            "Performance of a contract or pre-contractual steps — to take your reservation, fulfil your pre-order and deliver the product.",
            "Legitimate interests — to keep the service secure, prevent abuse, and improve reliability, balanced against your rights and expectations.",
            "Legal obligation — for accounting, tax and consumer-protection record-keeping once payments are enabled.",
          ]}
        />
      </Section>

      <Section title="7. Who we share data with">
        <p>
          We share data only with trusted service providers who process data
          on our behalf under appropriate contractual safeguards, including:
        </p>
        <SubList
          items={[
            "Hosting and database providers.",
            "AI / voice providers (speech-to-text, language model, text-to-speech) used by the live demo.",
            "Email delivery providers — we use Resend (resend.com) to send the waitlist confirmation email to you and the internal notification to our sales inbox. Resend processes the email address, name and content of those messages on our behalf.",
            "Payment providers (once pre-sale is enabled).",
            "Shipping and logistics partners for fulfilment.",
          ]}
        />
        <p>
          We do not sell your personal data. We may disclose data if required
          by law, legal process, or to protect the rights, safety and
          integrity of our service and users.
        </p>
      </Section>

      <Section title="8. International transfers">
        <p>
          Some service providers may be located outside your country. Where
          required, we rely on appropriate safeguards (such as standard
          contractual clauses) for international transfers of personal data.
        </p>
      </Section>

      <Section title="9. Data retention">
        <p>
          We keep your waitlist record only for as long as it is needed for
          launch and waitlist purposes — that is, until you ask to be removed,
          or until the Founders Edition pre-sale and launch communications
          have concluded and the data is no longer needed for the purposes
          described above. Other personal data is kept only as long as
          necessary for the original purpose of collection or to comply with
          legal obligations, resolve disputes and enforce agreements.
          Specific retention periods will be confirmed before payments open.
        </p>
      </Section>

      <Section title="9b. Unsubscribing & removal from the waitlist">
        <p>
          You can ask to be removed from the waitlist at any time, free of
          charge, by emailing{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:contact@ivory-pulse.com"
          >
            contact@ivory-pulse.com
          </a>{" "}
          or{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:support@ivory-pulse.com"
          >
            support@ivory-pulse.com
          </a>{" "}
          with the subject "remove me". We will delete your waitlist record
          and stop sending you waitlist, launch and product availability
          updates. Withdrawing consent does not affect the lawfulness of
          processing carried out before you withdrew it.
        </p>
      </Section>

      <Section title="10. Your rights">
        <p>
          Subject to applicable law (including the GDPR where it applies), you
          may have the right to:
        </p>
        <SubList
          items={[
            "Access the personal data we hold about you.",
            "Request correction of inaccurate or incomplete data.",
            "Request deletion of your data where legally applicable.",
            "Object to, or restrict, certain processing.",
            "Withdraw your consent at any time (this does not affect prior lawful processing).",
            "Lodge a complaint with your local data-protection authority.",
          ]}
        />
        <p>
          To exercise any of these rights, please email{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:contact@ivory-pulse.com"
          >
            contact@ivory-pulse.com
          </a>
          .
        </p>
      </Section>

      <Section title="11. Cookies, local storage and session IDs">
        <p>
          The site uses a minimal set of technical cookies and browser storage
          strictly necessary to operate the site, including a short-lived
          session identifier used by the voice demo. If we add analytics or
          third-party measurement tooling, we will update this policy and,
          where required by law, present a consent control before non-essential
          tracking begins.
        </p>
      </Section>

      <Section title="11b. Security">
        <p>
          We apply reasonable technical and organisational measures to
          protect your personal data, including encryption in transit (HTTPS),
          access controls on backend systems and database, server-side-only
          handling of API keys and credentials, and ongoing review of our
          third-party processors. No system can guarantee absolute security,
          but we work to keep your data protected and to respond promptly if
          we become aware of an incident affecting it.
        </p>
      </Section>

      <Section title="12. Children">
        <p>
          Ivory Pulse is not directed to children. We do not knowingly collect
          personal data from individuals under the age required by local law
          to provide consent. If you believe a child has provided us with
          personal data, please contact us and we will take appropriate steps.
        </p>
      </Section>

      <Section title="13. Changes to this Privacy Policy">
        <p>
          We may update this Privacy Policy to reflect changes in the service,
          legal requirements or operational practice. Material changes will be
          announced by email or on the website before they take effect.
        </p>
      </Section>

      <Section title="14. Contact">
        <p>
          For any privacy-related question, please email{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:contact@ivory-pulse.com"
          >
            contact@ivory-pulse.com
          </a>
          . For account or product help, write to{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:support@ivory-pulse.com"
          >
            support@ivory-pulse.com
          </a>
          . For Founders Edition reservations, write to{" "}
          <a
            className="underline decoration-white/30 hover:decoration-white"
            href="mailto:sales@ivory-pulse.com"
          >
            sales@ivory-pulse.com
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}
